import { Routes } from "~/routes";
import { Link } from "~/components/Link";
import {
    faBook,
    faCog,
    faDatabase,
    faKey,
    faPlug,
    faRocket,
    faUserShield,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "~/lib/utils";
import { Anchor } from "~/components/Anchor";
import { RWA_URLS } from "~/consts";
import { ApiToolsUserEvents } from "~/modules/analytics/events/api_tools";
import { FeatureNames } from "~/modules/feature_flag/flags";
import { FeatureFlag } from "~/modules/feature_flag/components/FeatureFlag";

// // // //

enum NavIcons {
    gettingStarted = "gettingStarted",
    apiKeys = "apiKeys",
    endpoints = "endpoints",
    mcp = "mcp",
    oauth = "oauth",
    settings = "settings",
    docs = "docs",
}

const mapIconToFontAwesomeIcon = (icon: NavIcons) => {
    switch (icon) {
        case NavIcons.gettingStarted:
            return faRocket;
        case NavIcons.apiKeys:
            return faKey;
        case NavIcons.endpoints:
            return faDatabase;
        case NavIcons.mcp:
            // return faServer;
            return faPlug;
        case NavIcons.oauth:
            return faUserShield;
        case NavIcons.settings:
            return faCog;
        case NavIcons.docs:
            return faBook;
    }
};

// ENHANCEMENT - add FeatureFlag / Access Control here
export const tabs: Array<{
    id:
        | "getting-started"
        | "api-keys"
        | "endpoints"
        | "mcp"
        | "oauth"
        | "settings"
        | "docs";
    name: string;
    url: string;
    icon: NavIcons;
    analyticsLabel: string;
    featureFlag?: FeatureNames;
    external?: boolean;
}> = [
    {
        name: "Getting Started",
        url: Routes.apiTools,
        id: "getting-started",
        icon: NavIcons.gettingStarted,
        analyticsLabel: ApiToolsUserEvents.GettingStartedClickNavLink,
    },
    {
        name: "MCP",
        url: Routes.toolsMcp,
        id: "mcp",
        icon: NavIcons.mcp,
        analyticsLabel: "ApiToolsUserEvents.McpClickNavLink",
        featureFlag: FeatureNames.enableExperimentalFeatures,
    },
    {
        name: "API Keys",
        url: Routes.apiToolsApiKeys,
        id: "api-keys",
        icon: NavIcons.apiKeys,
        analyticsLabel: ApiToolsUserEvents.ApiKeysClickNavLink,
    },
    {
        name: "Endpoints",
        url: Routes.apiToolsV4Assets,
        id: "endpoints",
        icon: NavIcons.endpoints,
        analyticsLabel: ApiToolsUserEvents.EndpointsClickNavLink,
    },
    {
        name: "Docs",
        url: RWA_URLS.docs,
        id: "docs",
        icon: NavIcons.docs,
        external: true,
        analyticsLabel: ApiToolsUserEvents.DocsClickNavLink,
    },
    {
        name: "Settings",
        url: Routes.oauthClients,
        id: "settings",
        icon: NavIcons.settings,
        analyticsLabel: "ApiToolsUserEvents.SettingsClickNavLink",
        featureFlag: FeatureNames.enableExperimentalFeatures,
    },
];

// // // //

export function ApiToolsNav(props: {
    page?:
        | "getting-started"
        | "api-keys"
        | "endpoints"
        | "mcp"
        | "oauth"
        | "settings";
    children: React.ReactNode;
}) {
    const { page = "endpoints" } = props;

    return (
        <div className="w-full flex justify-center">
            <div className="w-full flex flex-col px-0">
                <main className="flex-1">
                    <div className="relative mx-auto">
                        <div className="pb-8">
                            <div className="block">
                                <div className="border-b flex items-center gap-8 relative">
                                    <div className="flex-1">
                                        <nav className="-mb-px flex space-x-8 items-center">
                                            {tabs.map((tab) => {
                                                const linkClassName = cn(
                                                    "whitespace-nowrap py-4 px-1 border-b-2 text-sm select-none",
                                                    {
                                                        "border-primary text-primary":
                                                            tab.id === page,
                                                        "border-transparent text-muted-foreground hover:border-muted-foreground hover:text-muted-foreground-hover":
                                                            tab.id !== page,
                                                    },
                                                );

                                                const linkContent = (
                                                    <>
                                                        <FontAwesomeIcon
                                                            icon={mapIconToFontAwesomeIcon(
                                                                tab.icon,
                                                            )}
                                                            className="h-4 w-4 mr-2"
                                                        />
                                                        {tab.name}
                                                    </>
                                                );

                                                if (tab.external) {
                                                    return (
                                                        <FeatureFlag
                                                            key={tab.id}
                                                            feature={
                                                                tab.featureFlag
                                                            }
                                                        >
                                                            <Anchor
                                                                key={tab.id}
                                                                href={tab.url}
                                                                openInNewTab
                                                                analyticsLabel={
                                                                    tab.analyticsLabel
                                                                }
                                                                className={
                                                                    linkClassName
                                                                }
                                                            >
                                                                {linkContent}
                                                            </Anchor>
                                                        </FeatureFlag>
                                                    );
                                                }

                                                return (
                                                    <FeatureFlag
                                                        key={tab.id}
                                                        feature={
                                                            tab.featureFlag
                                                        }
                                                    >
                                                        <Link
                                                            key={tab.id}
                                                            href={tab.url}
                                                            analyticsLabel={
                                                                tab.analyticsLabel
                                                            }
                                                            className={
                                                                linkClassName
                                                            }
                                                        >
                                                            {linkContent}
                                                        </Link>
                                                    </FeatureFlag>
                                                );
                                            })}
                                        </nav>
                                    </div>
                                </div>
                            </div>

                            {<>{props.children}</>}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}
